import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, CalendarDays, Loader2 } from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { usePageMeta, SITE_PUBLIC_URL } from "../components/site/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); } catch { return ""; } };

const md = {
  h1: (props) => <h2 className="font-display text-2xl font-bold text-[#133955] mt-8 mb-3" {...props} />,
  h2: (props) => <h2 className="font-display text-2xl font-bold text-[#133955] mt-8 mb-3" {...props} />,
  h3: (props) => <h3 className="font-display text-xl font-bold text-[#133955] mt-6 mb-2" {...props} />,
  p: (props) => <p className="text-base text-[#173042]/85 leading-relaxed mb-4" {...props} />,
  ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-1.5 text-[#173042]/85" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-1.5 text-[#173042]/85" {...props} />,
  a: (props) => <a className="text-[#54BCE6] font-semibold hover:text-[#133955] underline" {...props} />,
  strong: (props) => <strong className="font-bold text-[#133955]" {...props} />,
  blockquote: (props) => <blockquote className="border-l-4 border-[#54BCE6] pl-4 my-4 text-[#173042]/70 italic" {...props} />,
  img: (props) => <img className="rounded-xl my-6 w-full" {...props} />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/posts/${slug}`)
      .then(({ data }) => setPost(data))
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  const coverUrl = post?.cover_file ? `${SITE_PUBLIC_URL}/api/files/${post.cover_file.file_id}` : null;
  usePageMeta({
    title: post ? `${post.title} | Home Pro Connect` : "Blog | Home Pro Connect",
    description: post?.meta_description || post?.excerpt || "",
    path: post ? `/blog/${post.slug}` : "/blog",
    image: coverUrl,
    type: "article",
    jsonLd: post ? {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.meta_description || post.excerpt || "",
      ...(coverUrl ? { image: coverUrl } : {}),
      datePublished: post.published_at,
      dateModified: post.updated_at || post.published_at,
      author: { "@type": "Organization", name: post.author_name || "Home Pro Connect" },
      publisher: { "@type": "Organization", name: "Home Pro Connect" },
      mainEntityOfPage: `${SITE_PUBLIC_URL}/blog/${post.slug}`,
    } : null,
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-3xl mx-auto px-6 pt-28 pb-12 md:pt-32 md:pb-16" data-testid="blog-post">
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-sm font-bold text-[#133955] hover:text-[#54BCE6] transition-colors mb-8">
          <ArrowLeft size={15} /> Back to all posts
        </Link>
        {loading ? (
          <div className="flex items-center gap-2 text-[#173042]/60"><Loader2 size={18} className="animate-spin" /> Loading…</div>
        ) : notFound || !post ? (
          <div className="rounded-2xl bg-[#EAF7FC] p-10 text-center" data-testid="blog-post-404">
            <p className="font-bold text-[#133955] text-lg">Post not found</p>
            <p className="text-[#173042]/60 text-sm mt-1">It may have been unpublished or the link is wrong.</p>
          </div>
        ) : (
          <article>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(post.tags || []).map((t) => (
                <span key={t} className="rounded-full bg-[#EAF7FC] text-[#133955] px-2.5 py-0.5 text-[11px] font-bold">{t}</span>
              ))}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#133955] leading-tight" data-testid="blog-post-title">{post.title}</h1>
            <p className="text-sm text-[#173042]/50 mt-4 flex items-center gap-1.5" data-testid="blog-post-meta">
              <CalendarDays size={13} /> {fmtDate(post.published_at)} · {post.author_name}
            </p>
            {post.cover_file && (
              <img
                src={`${API}/files/${post.cover_file.file_id}`}
                alt={post.title}
                className="w-full rounded-2xl mt-8 object-cover max-h-[420px]"
                data-testid="blog-post-cover"
              />
            )}
            <div className="mt-8" data-testid="blog-post-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{post.body || ""}</ReactMarkdown>
            </div>

            {/* CTA block — appended below the post body (post content itself is untouched) */}
            {(() => {
              const TAG_SERVICE = {
                roofing: ["roofing", "Roofing"], plumbing: ["plumbing", "Plumbing"],
                hvac: ["hvac", "HVAC"], flooring: ["flooring", "Flooring"], painting: ["painting", "Painting"],
              };
              const tags = post.tags || [];
              const match = Object.entries(TAG_SERVICE).find(([, names]) => tags.some((t) => names.includes(t)));
              const serviceHref = match ? `/sc/home-services/${match[0]}/` : "/sc/major-projects/";
              const serviceLabel = match ? `${match[0].charAt(0).toUpperCase() + match[0].slice(1)} services` : "major projects";
              return (
                <div className="mt-12 rounded-2xl bg-[#133955] text-white p-8" data-testid="blog-post-cta">
                  <h2 className="font-display text-2xl font-bold">Planning a home project?</h2>
                  <p className="mt-2 text-white/80 text-[15px] leading-relaxed">
                    Tell us about it once — Home Pro Connect will help connect you with the right pro
                    for the job, from major renovations to everyday home services.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <a href="/#request" data-testid="blog-cta-request" className="inline-flex items-center gap-2 rounded-full bg-[#54BCE6] text-[#133955] px-6 py-3 font-bold hover:bg-white transition-colors">
                      Request a Connection <ArrowLeft size={16} className="rotate-180" />
                    </a>
                    <a href={serviceHref} data-testid="blog-cta-service" className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 text-white px-6 py-3 font-bold hover:border-white hover:bg-white/10 transition-colors">
                      Explore {serviceLabel}
                    </a>
                  </div>
                </div>
              );
            })()}
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
